import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Link,
  Text,
} from '@chakra-ui/react';
import SettingsEthernetSharpIcon from '@gamma/ui/icons/SettingsEthernetSharpIcon';
import { Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { i18n } from '@investigator/shared/localization';
import { formatDateRangeDisplay } from '@investigator/shared/utils';
import {
  useEntitiesContext,
  useEntityEdrContext,
  useEntityAgenticTriageReportContext,
} from '../../context';
import { useAgenticTriageSettings } from '../../hooks';
import { EntityFieldLayout } from '../../types';
import { formatEntityFields } from '../../utils';
import { ChatGPTIcon } from '../ChatGPTIcon';
import { EntityInformationField } from './EntityInformationField';
import { useParseBackticks } from '@investigator/shared/hooks';

const AI_SUMMARY_TRUNCATE_LENGTH = [69, 163];

export interface EntityInformationPanelProps {
  isFullWidth?: boolean;
}

export const EntityInformationPanel = ({
  isFullWidth = false,
}: EntityInformationPanelProps): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const { edrData, loading: edrLoading } = useEntityEdrContext();
  const { report } = useEntityAgenticTriageReportContext();
  const { agenticTriageEnabled } = useAgenticTriageSettings();
  const { parseBackticks } = useParseBackticks();
  const modalCopy = i18n.pages.entities.entityAnalysisModal;

  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  // Loading states from logscale queries
  const {
    isSimpleSearchMetaLoading,
    isSimpleSearchOrigLoading,
    isSimpleSearchRespLoading,
    isSimpleSearchDhcpLoading,
    isSimpleSearchKnownLoading,
  } = selectedEntity || {};

  const aiSummary = report?.entity_info?.summary ?? '';

  const entityInformationFields = useMemo(
    () =>
      formatEntityFields({
        edrData: edrData ?? null,
        selectedEntity,
        loadingStates: {
          edrLoading,
          metaLoading: isSimpleSearchMetaLoading,
          origLoading: isSimpleSearchOrigLoading,
          respLoading: isSimpleSearchRespLoading,
          dhcpLoading: isSimpleSearchDhcpLoading,
          knownLoading: isSimpleSearchKnownLoading,
        },
        layout: isFullWidth
          ? EntityFieldLayout.FullWidth
          : EntityFieldLayout.HalfWidth,
      }),
    [
      edrData,
      selectedEntity,
      edrLoading,
      isSimpleSearchMetaLoading,
      isSimpleSearchOrigLoading,
      isSimpleSearchRespLoading,
      isSimpleSearchDhcpLoading,
      isSimpleSearchKnownLoading,
      isFullWidth,
    ],
  );

  // Calculate date range display from actual timestamps being used
  const dateRangeDisplay = formatDateRangeDisplay(
    selectedEntityStart,
    selectedEntityEnd,
  );

  // Truncate AI summary for collapsed state (different lengths for each layout)
  const maxChars = AI_SUMMARY_TRUNCATE_LENGTH[isFullWidth ? 1 : 0];
  const isTruncated = aiSummary ? aiSummary.length > maxChars : false;
  const moreLessText = aiSummaryExpanded ? 'less' : 'more';
  const aiSummaryDisplay =
    aiSummary && aiSummaryExpanded
      ? aiSummary
      : aiSummary?.slice(0, maxChars) + (isTruncated ? '...' : '');

  return (
    <Panel w="100%">
      <PanelHeader pb={0}>
        <PanelHeading>
          <HStack spacing={2}>
            <SettingsEthernetSharpIcon />
            <Text>{modalCopy.entityInformation}</Text>
            {dateRangeDisplay && (
              <Text color="gray.400" fontWeight="normal">
                {dateRangeDisplay}
              </Text>
            )}
          </HStack>
        </PanelHeading>
      </PanelHeader>
      <Box data-testid="entity-information-panel">
        {agenticTriageEnabled && aiSummary && (
          <Flex
            mb={4}
            gap={2}
            align={aiSummaryExpanded ? 'flex-start' : 'center'}
          >
            <Box flexShrink={0}>
              <ChatGPTIcon boxSize={5} />
            </Box>
            <Box>
              <Text color="text.primary" textStyle="body-md">
                {parseBackticks(aiSummaryDisplay)}
                {isTruncated && (
                  <Link
                    color="blue.400"
                    ml={1}
                    textStyle="body-md"
                    onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                    cursor="pointer"
                  >
                    {moreLessText}
                  </Link>
                )}
              </Text>
            </Box>
          </Flex>
        )}
        {isFullWidth ? (
          <Grid
            templateColumns="repeat(7, 1fr)"
            gap={4}
            data-testid="entity-info-fields-grid"
          >
            {entityInformationFields.map((field) => (
              <GridItem key={field.label}>
                <EntityInformationField
                  field={field}
                  isInline={false}
                  skeletonWidth="80px"
                />
              </GridItem>
            ))}
          </Grid>
        ) : (
          <Grid
            templateColumns="repeat(2, 1fr)"
            gap={3}
            data-testid="entity-info-fields-grid"
          >
            {entityInformationFields.map((field) => (
              <GridItem key={field.label}>
                <EntityInformationField
                  field={field}
                  isInline={true}
                  skeletonWidth="100px"
                />
              </GridItem>
            ))}
          </Grid>
        )}
      </Box>
    </Panel>
  );
};
